import React, { useState, useEffect } from 'react';
import useAIVisualSettings from '../../hooks/useAIVisualSettings';
import AIOrb from './AIOrb';
import AIAvatar from './AIAvatar';
import { RotateCcw, Volume2, VolumeX } from 'lucide-react';

const STYLE_OPTIONS = [
    { key: 'morphing-gradient', label: 'Morphing Orb', desc: 'Smooth organic shape-shifting', tag: 'Default' },
    { key: 'siri-noise', label: 'Noise Orb', desc: 'Siri-style flowing surface', tag: 'Premium' },
    { key: 'particle-cloud', label: 'Particle Cloud', desc: 'Floating particle sphere', tag: null },
    { key: 'waveform-ring', label: 'Waveform Ring', desc: 'Circular audio visualizer', tag: null },
    { key: 'simple-pulse', label: 'Simple Pulse', desc: 'Clean minimal glow', tag: 'Lightest' },
    { key: 'avatar', label: 'AI Avatar', desc: 'Illustrated talking head', tag: 'Beta' },
];

const SIZE_OPTIONS = [
    { key: 'sm', label: 'Small', px: 48 },
    { key: 'md', label: 'Medium', px: 64 },
    { key: 'lg', label: 'Large', px: 80 },
];

export default function AIVisualSettings() {
    const { settings, updateSettings, resetSettings, sizePx } = useAIVisualSettings();
    const [voices, setVoices] = useState([]);
    const [demoAmplitude, setDemoAmplitude] = useState(0);

    // Load available TTS voices
    useEffect(() => {
        const loadVoices = () => {
            const v = window.speechSynthesis?.getVoices() || [];
            setVoices(v.filter(voice => voice.lang.startsWith('en')));
        };
        loadVoices();
        window.speechSynthesis?.addEventListener?.('voiceschanged', loadVoices);
        return () => window.speechSynthesis?.removeEventListener?.('voiceschanged', loadVoices);
    }, []);

    // Animate demo preview
    useEffect(() => {
        let frame;
        let t = 0;
        const animate = () => {
            t += 0.05;
            setDemoAmplitude(Math.abs(Math.sin(t) * Math.sin(t * 1.7)) * 0.8);
            frame = requestAnimationFrame(animate);
        };
        frame = requestAnimationFrame(animate);
        return () => cancelAnimationFrame(frame);
    }, []);

    return (
        <div className="space-y-8">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200">
                <div>
                    <h3 className="text-sm font-semibold text-stone-900">Floating AI Assistant</h3>
                    <p className="text-xs text-stone-500 mt-0.5">Show AI entity on all pages</p>
                </div>
                <button
                    onClick={() => updateSettings({ enabled: !settings.enabled })}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? 'bg-violet-600' : 'bg-stone-300'}`}
                >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                </button>
            </div>

            {/* Style Picker */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-3">Animation Style</h3>
                <div className="grid grid-cols-3 gap-3">
                    {STYLE_OPTIONS.map(opt => {
                        const isSelected = settings.style === opt.key;
                        return (
                            <button
                                key={opt.key}
                                onClick={() => updateSettings({ style: opt.key })}
                                className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                                    isSelected
                                        ? 'border-violet-500 bg-violet-50/50 shadow-sm'
                                        : 'border-stone-200 hover:border-stone-300 bg-white'
                                }`}
                            >
                                {opt.tag && (
                                    <span className={`absolute top-2 right-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full ${
                                        opt.tag === 'Default' ? 'bg-violet-100 text-violet-600'
                                            : opt.tag === 'Premium' ? 'bg-amber-100 text-amber-700'
                                                : opt.tag === 'Lightest' ? 'bg-emerald-100 text-emerald-700'
                                                    : 'bg-stone-100 text-stone-600'
                                    }`}>
                                        {opt.tag}
                                    </span>
                                )}
                                {/* Mini preview */}
                                <div className="flex justify-center mb-3 h-12 items-center">
                                    {opt.key === 'avatar' ? (
                                        <AIAvatar amplitude={demoAmplitude} isSpeaking size={40} provider="built-in" />
                                    ) : (
                                        <AIOrb style={opt.key} amplitude={demoAmplitude} isSpeaking size={40} />
                                    )}
                                </div>
                                <p className="text-xs font-semibold text-stone-900">{opt.label}</p>
                                <p className="text-[10px] text-stone-500 mt-0.5">{opt.desc}</p>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Size */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-3">Size</h3>
                <div className="flex gap-2">
                    {SIZE_OPTIONS.map(opt => (
                        <button
                            key={opt.key}
                            onClick={() => updateSettings({ size: opt.key })}
                            className={`flex-1 px-4 py-2.5 rounded-lg text-xs font-medium border transition-all ${
                                settings.size === opt.key
                                    ? 'border-violet-500 bg-violet-50 text-violet-700'
                                    : 'border-stone-200 text-stone-600 hover:border-stone-300'
                            }`}
                        >
                            {opt.label} ({opt.px}px)
                        </button>
                    ))}
                </div>
            </div>

            {/* TTS */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-3">Text-to-Speech</h3>
                <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                        <div className="flex items-center gap-2">
                            {settings.ttsEnabled ? <Volume2 className="w-4 h-4 text-violet-600" /> : <VolumeX className="w-4 h-4 text-stone-400" />}
                            <span className="text-xs font-medium text-stone-700">Read AI responses aloud</span>
                        </div>
                        <button
                            onClick={() => updateSettings({ ttsEnabled: !settings.ttsEnabled })}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${settings.ttsEnabled ? 'bg-violet-600' : 'bg-stone-300'}`}
                        >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform shadow-sm ${settings.ttsEnabled ? 'translate-x-4.5' : 'translate-x-0.5'}`} />
                        </button>
                    </div>

                    {settings.ttsEnabled && voices.length > 0 && (
                        <select
                            value={settings.ttsVoice}
                            onChange={e => updateSettings({ ttsVoice: e.target.value })}
                            className="w-full px-3 py-2 text-xs border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500/20"
                        >
                            <option value="">System Default</option>
                            {voices.map(v => (
                                <option key={v.name} value={v.name}>{v.name}</option>
                            ))}
                        </select>
                    )}
                </div>
            </div>

            {/* Simli.com Stub */}
            {settings.style === 'avatar' && (
                <div className="p-4 rounded-xl border border-dashed border-violet-300 bg-violet-50/30">
                    <h4 className="text-xs font-semibold text-violet-800 mb-1">Photorealistic Avatar (Coming Soon)</h4>
                    <p className="text-[10px] text-violet-600 leading-relaxed">
                        Upgrade to a lifelike AI avatar powered by Simli.com. Real-time lip sync with photorealistic rendering. Available in a future update.
                    </p>
                </div>
            )}

            {/* Live Preview */}
            <div>
                <h3 className="text-sm font-semibold text-stone-900 mb-3">Preview</h3>
                <div className="flex items-center justify-center p-8 bg-stone-50 rounded-xl border border-stone-200">
                    {settings.style === 'avatar' ? (
                        <AIAvatar amplitude={demoAmplitude} isSpeaking size={sizePx} provider={settings.avatarProvider} />
                    ) : (
                        <AIOrb style={settings.style} amplitude={demoAmplitude} isSpeaking size={sizePx} />
                    )}
                </div>
            </div>

            {/* Reset */}
            <button
                onClick={resetSettings}
                className="flex items-center gap-2 text-xs text-stone-500 hover:text-stone-700 transition-colors"
            >
                <RotateCcw className="w-3.5 h-3.5" />
                Reset to defaults
            </button>
        </div>
    );
}
